import React, { useState, useEffect } from 'react';
import { ApiRequestNoLoder, ApiRequest } from '../services/api';

interface AppVersion {
  id: number;
  version_name: string;
  version_code: number;
  platform: string;
  min_supported_version?: number;
  is_force_update: boolean;
  is_current: boolean;
  is_active: boolean;
  release_notes?: string;
  download_url?: string;
}

const Version = () => {
  const [versions, setVersions] = useState<AppVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState('ANDROID');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({
    version_name: '',
    version_code: '',
    platform: 'ANDROID',
    min_supported_version: '',
    is_force_update: false,
    is_current: false,
    release_notes: '',
    download_url: ''
  });

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const response = await ApiRequestNoLoder('GET', `app-versions/?platform=${platform}`);
      setVersions(Array.isArray(response) ? response : []);
    } catch (error) {
      setVersions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
  }, [platform]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const params: any = {
        version_name: form.version_name,
        version_code: parseInt(form.version_code),
        platform: form.platform,
        is_force_update: form.is_force_update,
        is_current: form.is_current
      };
      
      if (form.min_supported_version) params.min_supported_version = parseInt(form.min_supported_version);
      if (form.release_notes) params.release_notes = form.release_notes;
      if (form.download_url) params.download_url = form.download_url;

      const queryString = Object.keys(params).map(key => `${key}=${encodeURIComponent(params[key])}`).join('&');

      if (editId) {
        await ApiRequest('PUT', `app-versions/${editId}?${queryString}`);
      } else {
        await ApiRequest('POST', `app-versions/?${queryString}`);
      }
      resetForm();
      fetchVersions();
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  const resetForm = () => {
    setForm({ version_name: '', version_code: '', platform: 'ANDROID', min_supported_version: '', is_force_update: false, is_current: false, release_notes: '', download_url: '' });
    setEditId(null);
    setShowForm(false);
  };

  const handleEdit = (v: AppVersion) => {
    setForm({
      version_name: v.version_name,
      version_code: String(v.version_code),
      platform: v.platform,
      min_supported_version: v.min_supported_version ? String(v.min_supported_version) : '',
      is_force_update: v.is_force_update,
      is_current: v.is_current,
      release_notes: v.release_notes || '',
      download_url: v.download_url || ''
    });
    setEditId(v.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Delete this version?')) {
      await ApiRequest('DELETE', `app-versions/${id}`);
      fetchVersions();
    }
  };

return (
  <div className="mt-12 space-y-6 animate-fade-in">
    <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-black text-white uppercase italic tracking-tighter">App Versions</h1>
        <button onClick={() => setShowForm(!showForm)} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase tracking-widest rounded-lg transition-all shadow-lg shadow-blue-600/20">
          {showForm ? 'Cancel' : '+ Add Version'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-8 bg-slate-950/50 border border-slate-800 p-6 rounded-xl space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Platform *</label>
              <select value={form.platform} onChange={(e) => setForm({...form, platform: e.target.value})} className="w-full p-3 bg-slate-950 border border-slate-800 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 transition-colors">
                <option value="ANDROID" className="bg-slate-900">Android</option>
                <option value="IOS" className="bg-slate-900">iOS</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Version Name *</label>
              <input type="text" value={form.version_name} onChange={(e) => setForm({...form, version_name: e.target.value})} placeholder="1.0.0" className="w-full p-3 bg-slate-950 border border-slate-800 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 transition-colors" required />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Version Code *</label>
              <input type="number" value={form.version_code} onChange={(e) => setForm({...form, version_code: e.target.value})} placeholder="100" className="w-full p-3 bg-slate-950 border border-slate-800 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 transition-colors" required />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Min Supported</label>
              <input type="number" value={form.min_supported_version} onChange={(e) => setForm({...form, min_supported_version: e.target.value})} placeholder="90" className="w-full p-3 bg-slate-950 border border-slate-800 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Download URL</label>
              <input type="url" value={form.download_url} onChange={(e) => setForm({...form, download_url: e.target.value})} placeholder="https://..." className="w-full p-3 bg-slate-950 border border-slate-800 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Release Notes</label>
            <textarea value={form.release_notes} onChange={(e) => setForm({...form, release_notes: e.target.value})} placeholder="What's new..." className="w-full p-3 bg-slate-950 border border-slate-800 rounded-lg text-white text-sm h-20 focus:outline-none focus:border-blue-500 transition-colors" />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_force_update} onChange={(e) => setForm({...form, is_force_update: e.target.checked})} className="w-4 h-4" />
              <span className="text-xs font-bold text-slate-300 uppercase">Force Update</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_current} onChange={(e) => setForm({...form, is_current: e.target.checked})} className="w-4 h-4" />
              <span className="text-xs font-bold text-slate-300 uppercase">Set as Current</span>
            </label>
          </div>
          <button type="submit" className="w-full bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase tracking-widest py-3 rounded-lg transition-all shadow-lg shadow-blue-600/20">
            {editId ? 'Update Version' : 'Create Version'}
          </button>
        </form>
      )}

      <div className="mb-6">
        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Filter by Platform:</label>
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="p-3 bg-slate-950 border border-slate-800 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 transition-colors">
          <option value="ANDROID" className="bg-slate-900">Android</option>
          <option value="IOS" className="bg-slate-900">iOS</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-400 text-sm font-bold uppercase">Loading versions...</div>
      ) : versions.length === 0 ? (
        <div className="text-center py-8 text-slate-600 text-sm font-bold uppercase">No versions found</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full text-left">
            <thead className="bg-slate-950">
              <tr>
                <th className="p-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Version</th>
                <th className="p-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Code</th>
                <th className="p-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Status</th>
                <th className="p-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id} className="border-t border-slate-800 hover:bg-slate-800/30 transition-colors">
                  <td className="p-4 text-sm font-bold text-white">{v.version_name}</td>
                  <td className="p-4 text-sm font-bold text-slate-300">{v.version_code}</td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      {v.is_current && <span className="px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded text-[10px] font-black uppercase">Current</span>}
                      {v.is_force_update && <span className="px-2 py-1 bg-red-500/10 text-red-400 border border-red-500/20 rounded text-[10px] font-black uppercase">Force</span>}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(v)} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-[10px] font-black uppercase transition-all">Edit</button>
                      <button onClick={() => handleDelete(v.id)} className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded text-[10px] font-black uppercase transition-all">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);
};

export default Version;
